import React, { useState, useEffect, useRef } from 'react';
import { Filter, X, Check, Search } from 'lucide-react';
import { useLocationFilter } from '@/contexts/LocationFilterContext';

const LocationFilterFloater: React.FC = () => {
  const {
    selectedLocationIds,
    availableLocations,
    isLoading,
    setSelectedLocationIds,
    clearFilters,
    hasActiveFilters,
  } = useLocationFilter();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Filter locations based on search term
  const filteredLocations = availableLocations.filter(location =>
    location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleLocation = (locationId: string) => {
    if (selectedLocationIds.includes(locationId)) {
      setSelectedLocationIds(selectedLocationIds.filter(id => id !== locationId));
    } else {
      setSelectedLocationIds([...selectedLocationIds, locationId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedLocationIds.length === filteredLocations.length) {
      setSelectedLocationIds([]);
    } else {
      setSelectedLocationIds(filteredLocations.map(loc => loc.id!).filter(Boolean));
    }
  };

  const getSelectedLocationNames = () => {
    return availableLocations
      .filter(loc => selectedLocationIds.includes(loc.id!))
      .map(loc => loc.name)
      .slice(0, 2);
  };

  const selectedNames = getSelectedLocationNames();
  const remainingCount = selectedLocationIds.length - selectedNames.length;

  return (
    <>
      {/* Floating Filter Button - Middle Right */}
      <div className="fixed top-1/2 right-6 -translate-y-1/2 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            relative flex items-center justify-center w-14 h-14 rounded-full shadow-lg
            transition-all duration-200 hover:scale-110
            ${hasActiveFilters 
              ? 'bg-primary-600 text-white hover:bg-primary-700' 
              : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300'
            }
          `}
          title={hasActiveFilters ? `${selectedLocationIds.length} location(s) selected` : 'Filter by location'}
        >
          <Filter className="w-6 h-6" />
          {hasActiveFilters && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold">
              {selectedLocationIds.length}
            </span>
          )}
        </button>
      </div>

      {/* Filter Panel */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4" onClick={() => setIsOpen(false)}>
          <div
            ref={panelRef}
            className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">Filter Locations</h3>
              </div>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search locations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Selected Locations Summary */}
            {hasActiveFilters && (
              <div className="px-4 py-2 bg-primary-50 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">
                    {selectedLocationIds.length} location{selectedLocationIds.length !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={clearFilters}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Clear all
                  </button>
                </div>
                {selectedNames.length > 0 && (
                  <div className="mt-1 text-xs text-gray-600">
                    {selectedNames.join(', ')}
                    {remainingCount > 0 && ` + ${remainingCount} more`}
                  </div>
                )}
              </div>
            )}

            {/* Locations List */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="text-center py-8 text-gray-500">Loading locations...</div>
              ) : filteredLocations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? 'No locations found matching your search.' : 'No locations available.'}
                </div>
              ) : (
                <>
                  {/* Select All / Deselect All */}
                  <div className="mb-3 pb-3 border-b border-gray-200">
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      {selectedLocationIds.length === filteredLocations.length ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>

                  <div className="space-y-2">
                    {filteredLocations.map((location) => {
                      const isSelected = selectedLocationIds.includes(location.id!);
                      return (
                        <label
                          key={location.id}
                          className={`
                            flex items-start space-x-3 p-3 rounded-lg border cursor-pointer
                            transition-colors
                            ${isSelected 
                              ? 'bg-primary-50 border-primary-300' 
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                            }
                          `}
                        >
                          <div className="flex items-center justify-center w-5 h-5 mt-0.5">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => location.id && toggleLocation(location.id)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className={`text-sm font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                                {location.name}
                              </span>
                              {isSelected && (
                                <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex gap-2 mt-1 text-xs text-gray-500">
                              {location.city && <span>{location.city}</span>}
                              {location.city && location.state && <span>•</span>}
                              <span>{location.state}</span>
                              <span>•</span>
                              <span>{location.country}</span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {selectedLocationIds.length > 0 && (
                  <span>{selectedLocationIds.length} selected</span>
                )}
              </div>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LocationFilterFloater;

